import { Component } from '@theme/component';
import { formatMoney } from '@theme/utilities';

/**
 * Adds a dual-thumb range control to the SPX price facet while keeping the
 * existing named text inputs as the single source submitted with the form.
 */
class SpxPriceRangeComponent extends Component {
  requiredRefs = ['minRange', 'maxRange', 'minOutput', 'maxOutput'];

  /** @type {HTMLInputElement | undefined} */
  #minTextInput;

  /** @type {HTMLInputElement | undefined} */
  #maxTextInput;

  /** @type {MutationObserver | undefined} */
  #valueObserver;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('input', this.handleRangeInput);
    this.addEventListener('keydown', this.preserveRangeKeyboard);
    this.addEventListener('pointerdown', this.#handleThumbActivation);
    this.addEventListener('focusin', this.#handleThumbActivation);
    this.#bindTextInputs();
    this.#syncFromTextInputs();
    this.#observeValueChanges();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.handleRangeInput);
    this.removeEventListener('keydown', this.preserveRangeKeyboard);
    this.removeEventListener('pointerdown', this.#handleThumbActivation);
    this.removeEventListener('focusin', this.#handleThumbActivation);
    this.#valueObserver?.disconnect();
    this.#valueObserver = undefined;
    this.#unbindTextInputs();
    super.disconnectedCallback();
  }

  #observeValueChanges() {
    this.#valueObserver?.disconnect();
    this.#valueObserver = new MutationObserver(() => {
      queueMicrotask(() => {
        this.#bindTextInputs();
        this.#syncFromTextInputs();
      });
    });
    this.#valueObserver.observe(this.closest('price-facet-component') ?? this, {
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'max'],
    });
  }

  #bindTextInputs() {
    const priceFacet = this.closest('price-facet-component');
    const minInput = priceFacet?.querySelector('[ref="minInput"]');
    const maxInput = priceFacet?.querySelector('[ref="maxInput"]');

    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;
    if (minInput === this.#minTextInput && maxInput === this.#maxTextInput) return;

    this.#unbindTextInputs();
    this.#minTextInput = minInput;
    this.#maxTextInput = maxInput;
    minInput.addEventListener('input', this.#handleTextInput);
    maxInput.addEventListener('input', this.#handleTextInput);
  }

  #unbindTextInputs() {
    this.#minTextInput?.removeEventListener('input', this.#handleTextInput);
    this.#maxTextInput?.removeEventListener('input', this.#handleTextInput);
    this.#minTextInput = undefined;
    this.#maxTextInput = undefined;
  }

  /** @param {InputEvent} event */
  #handleTextInput = (event) => {
    this.#syncFromTextInputs(event.target);
  };

  /**
   * Synchronizes the visual slider while a thumb is moving. The native range
   * change event is allowed to bubble to price-facet-component, which performs
   * the existing URL update and section render once the drag is committed.
   * @param {InputEvent} event
   */
  handleRangeInput(event) {
    const target = event.target;
    const { minRange, maxRange } = this.refs;
    if (!(target instanceof HTMLInputElement)) return;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    let minValue = Number(minRange.value);
    let maxValue = Number(maxRange.value);

    if (target === minRange) {
      minValue = Math.min(minValue, maxValue);
      minRange.value = this.#formatInputValue(minValue);
    } else if (target === maxRange) {
      maxValue = Math.max(maxValue, minValue);
      maxRange.value = this.#formatInputValue(maxValue);
    } else {
      return;
    }

    this.#writeTextInputs(minValue, maxValue);
    this.#updatePresentation(minValue, maxValue);

    if (minValue === maxValue) {
      this.dataset.activeThumb = target === minRange ? 'max' : 'min';
    } else {
      this.dataset.activeThumb = target.dataset.thumb ?? '';
    }
  }

  /** @param {PointerEvent | FocusEvent} event */
  #handleThumbActivation = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'range') return;
    this.dataset.activeThumb = target.dataset.thumb ?? '';
  };

  /** @param {KeyboardEvent} event */
  preserveRangeKeyboard(event) {
    if (event.target instanceof HTMLInputElement && event.target.type === 'range') {
      event.stopPropagation();
    }
  }

  /** @param {EventTarget | null} [source] */
  #syncFromTextInputs(source) {
    const { minRange, maxRange } = this.refs;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;
    if (!this.#minTextInput || !this.#maxTextInput) return;

    const rangeMax = Number(maxRange.max);
    if (!Number.isFinite(rangeMax) || rangeMax <= 0) return;

    let minValue = this.#parseInputValue(this.#minTextInput.value, 0);
    let maxValue = this.#parseInputValue(this.#maxTextInput.value, rangeMax);
    minValue = this.#clamp(minValue, 0, rangeMax);
    maxValue = this.#clamp(maxValue, 0, rangeMax);

    if (minValue > maxValue) {
      if (source === this.#maxTextInput) {
        maxValue = minValue;
      } else {
        minValue = maxValue;
      }
    }

    minRange.value = this.#formatInputValue(minValue);
    maxRange.value = this.#formatInputValue(maxValue);
    this.#writeTextInputs(minValue, maxValue);
    this.#updatePresentation(minValue, maxValue);

    if (minValue === maxValue && !this.dataset.activeThumb) {
      this.dataset.activeThumb = 'min';
    }
  }

  /** @param {number} minValue @param {number} maxValue */
  #writeTextInputs(minValue, maxValue) {
    if (!this.#minTextInput || !this.#maxTextInput) return;
    const rangeMax = Number(this.refs.maxRange instanceof HTMLInputElement ? this.refs.maxRange.max : 0);

    this.#minTextInput.value = minValue <= 0 ? '' : this.#formatInputValue(minValue);
    this.#maxTextInput.value = maxValue >= rangeMax ? '' : this.#formatInputValue(maxValue);
  }

  /** @param {number} minValue @param {number} maxValue */
  #updatePresentation(minValue, maxValue) {
    const { minOutput, maxOutput, maxRange } = this.refs;
    if (!(minOutput instanceof HTMLOutputElement) || !(maxOutput instanceof HTMLOutputElement)) return;
    if (!(maxRange instanceof HTMLInputElement)) return;

    const formattedMin = this.#formatCurrency(minValue);
    const formattedMax = this.#formatCurrency(maxValue);
    minOutput.value = formattedMin;
    maxOutput.value = formattedMax;

    const minRange = this.refs.minRange;
    if (minRange instanceof HTMLInputElement) minRange.setAttribute('aria-valuetext', formattedMin);
    maxRange.setAttribute('aria-valuetext', formattedMax);
  }

  /** @param {string} value @param {number} fallback */
  #parseInputValue(value, fallback) {
    if (value.trim() === '') return fallback;
    const parsed = Number(formatMoney(value));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /** @param {number} value */
  #formatInputValue(value) {
    return String(Math.round(value * 100) / 100);
  }

  /** @param {number} value */
  #formatCurrency(value) {
    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const currency = this.dataset.currency;
    if (!currency) return this.#formatInputValue(value);

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return this.#formatInputValue(value);
    }
  }

  /** @param {number} value @param {number} min @param {number} max */
  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

if (!customElements.get('spx-price-range')) {
  customElements.define('spx-price-range', SpxPriceRangeComponent);
}
